import { ReactNode } from "react";
import i18n from "i18n";
import {
  IContentItem,
  NotificationContentTypeEnum
} from "Data/interfaces/Notification/INotification";
import { Icon } from "Components/UI";
import Ellipsis from "Components/UI/Ellipsis";
import { dateToLocaleString } from "Utils/DateUtils";
import { GetAvatar } from "Utils/generateThumbnail";
import { IColorsTheme } from "Static/styles/themes/default";
import {
  TextboxContainer,
  ImagesContainer,
  RefContainer,
  ValueChangedContainer,
  DateContainer
} from "./styles.content";

import imgPlaceholder from 'Static/images/image_placeholder.webp';

interface IObjContentParams {
  content: IContentItem[];
  className: string | null;
  theme?: IColorsTheme;
  aggIds?: string[];
}

type ObjContentType = Record<
  keyof typeof NotificationContentTypeEnum,
  (params: IObjContentParams) => ReactNode
>;

export const objContent: ObjContentType = {
  'ref': ({ content, className, aggIds }) => {
    if (!!aggIds && aggIds.length > 0) {
      // TOOD: Avatars multiple implements
      // if (content.every(item => item.hasAvatar)) {
      //   const showLimit = 5;
      //   return (
      //     <RefContainer>
      //       <Tooltip title={content.map(item => item.value).join(', ')}>
      //         <div className="refMultipleContainer">
      //           {content.map((item, i) => (
      //             <div key={`refMultiple-${i}`} className="refMultiple" >
      //               <GetAvatar
      //                 name={item?.value || ''}
      //                 thumbSrc={item?.url || ''}
      //                 size={16}
      //                 fontSize={16}
      //                 zIndex={i + 1}
      //                 marginTop="-4px"
      //               />
      //             </div>
      //           ))}
      //           {content.length > showLimit && (
      //             <span className="refMore">+{content.length - showLimit}</span>
      //           )}
      //         </div>
      //       </Tooltip>
      //     </RefContainer>
      //   )
      // }

      const item = content[0];
      return (
        <RefContainer multiple>
          <div className={`ref ${className || ''} ${item?.valueClass || ''}`}>
            {!!item?.icon && (
              <Icon
                iconClassName={item.icon}
                customSize={12}
                className="refIcon"
              />
            )}
            {!!item?.id && <span className="refId">{`#${item.id}`}</span>}
            {!!item?.value && (
              <Ellipsis
                phrase={item?.value || ''}
                maxLength={33}
                className={`refValue ${item?.valueClass || ''}`}
              />
            )}
          </div>
          <div className="ref refShowLimit">
            <span className="refValue refValueShowLimit">
              {`+${aggIds.length}`}
            </span>
          </div>
        </RefContainer>
      )
    }

    return (
      <RefContainer>
        {content.map((item, i) => (
          <div
            key={`ref-${i}`}
            className={`ref ${className || ''} ${item?.valueClass || ''}`}
          >
            {!!item?.icon && (
              <Icon
                iconClassName={item.icon}
                customSize={12}
                className="refIcon"
              />
            )}
            {!!item?.id && <span className="refId">{`#${item.id}`}</span>}
            {item?.hasAvatar && (
              <GetAvatar
                name={item?.value || ''}
                thumbSrc={item?.url || ''}
                size={16}
                fontSize={16}
              />
            )}
            {!!item?.value && (
              <Ellipsis
                phrase={item?.value || ''}
                maxLength={33}
                className={`refValue ${item?.valueClass || ''}`}
              />
            )}
            {!!item?.iconFooter && (
              <Icon
                iconClassName={item.iconFooter}
                customSize={12}
                className="refIconFooter"
              />
            )}
          </div>
        ))}
      </RefContainer>
    )
  },
  'valueChanged': ({ content, className }) => (
    <ValueChangedContainer>
      {content.map((item, i) => {
        const hasOnlyNewValue = item?.newValue && !item?.label && !item?.oldValue;

        return (
          <div
            key={`valueChanged-${i}`}
            className={`valueChanged ${className || ''}`}
          >
            {item?.label && (
              <span className="valueChangedLabel">
                {item.label}
              </span>
            )}
            {item.oldValue && (
              <span className={`valueChangedOldValue ${item?.oldValueClass || ''}`}>
                {item.oldValue}
              </span>
            )}
            {item.oldValue && item.newValue && (
              <Icon
                icon="rightArrow"
                customSize={16}
                className="valueChangedIcon"
              />
            )}
            {item.newValue && (
              <span className={`valueChangedNewValue ${item?.newValueClass || ''} ${hasOnlyNewValue ? 'only-new-value' : ''}`}>
                {item.newValue}
              </span>
            )}
          </div>
        )
      })}
    </ValueChangedContainer>
  ),
  'images': ({ content, className }) => {
    const limit = 3;
    if (content.length > limit) {
      const showContent = content.slice(0, limit);
      const showLimit = content.length - limit;

      return (
        <ImagesContainer>
          {showContent.map((item, i) => (
            <div
              key={`images-limit-${i}`}
              className={`images ${className || ''}`}
            >
              <img
                src={item.url}
                alt=""
                onError={(e) => {
                  e.currentTarget.src = imgPlaceholder;
                }}
              />
              {i === showContent.length - 1 && (
                <span className="imagesMore">+{showLimit}</span>
              )}
            </div>
          ))}
        </ImagesContainer>
      )
    }

    return (
      <ImagesContainer>
        {content.map((item, i) => (
          <div
            key={`images-${i}`}
            className={`images ${className || ''}`}
          >
            <img
              src={item.url}
              alt=""
              onError={(e) => {
                e.currentTarget.src = imgPlaceholder;
              }}
            />
          </div>
        ))}
      </ImagesContainer>
    )
  },
  'textbox': ({ content, className }) => (
    <TextboxContainer>
      {content.map((item, i) => (
        <div
          key={`textbox-${i}`}
          className={`textbox ${className || ''}`}
        >
          {!!item?.icon && (
            <Icon
              iconClassName={item.icon}
              customSize={12}
              className="textboxIcon"
            />
          )}
          {(!!item?.value || item?.value === "") && (
            <Ellipsis
              phrase={item?.value || i18n.t('sharedModals:common.empty')}
              maxLength={115}
              className={`textboxValue ${item?.value === "" ? "textboxValueEmpty" : ""}`}
            />
          )}
          {!!item?.label && (
            <Ellipsis
              phrase={item.label || i18n.t('sharedModals:common.empty')}
              maxLength={115}
              className="textboxLabel"
            />
          )}
          {!!item?.date && (
            <span className="textboxLabel">
              {dateToLocaleString(item.date)}
            </span>
          )}
        </div>
      ))}
    </TextboxContainer>
  ),
  'date': ({ content, className }) => (
    <DateContainer>
      {content.map((item, i) => (
        <div
          key={`date-${i}`}
          className={`date ${className || ''}`}
        >
          <Icon
            icon="calendar"
            customSize={12}
            className="dateIcon"
          />
          {!!item?.dueDate && (
            <span className="dateLabel">
              {dateToLocaleString(item?.dueDate)}
            </span>
          )}
        </div>
      ))}
    </DateContainer>
  ),
}
